import Shepherd from "shepherd.js";

/**
 * App Overview Tour Configuration
 *
 * Defines the steps for the main app onboarding tour.
 * Guides users through sidebar navigation, key features, and navigation.
 */
export function createAppOverviewTour(): InstanceType<typeof Shepherd.Tour> {
  const tour = new Shepherd.Tour({
    defaultStepOptions: {
      cancelIcon: {
        enabled: true,
      },
      scrollTo: { behavior: "smooth", block: "center" },
      classes: "shepherd-theme-custom",
      when: {
        cancel: () => {
          tour.cancel();
        },
      },
    },
    useModalOverlay: true,
  });

  // Step 1: Welcome - Sidebar logo
  tour.addStep({
    id: "welcome",
    title: "Welcome to Vortal!",
    text: "Let's take a quick tour to show you around the platform.",
    attachTo: {
      element: '[data-tour="sidebar-logo"]',
      on: "right",
    },
    showOn() {
      return document.querySelector('[data-tour="sidebar-logo"]') !== null;
    },
    buttons: [
      {
        text: "Skip",
        action: tour.cancel,
        classes: "shepherd-button-secondary",
      },
      {
        text: "Next",
        action: tour.next,
      },
    ],
  });

  // Step 2: Sidebar navigation
  tour.addStep({
    id: "sidebar-nav",
    title: "Navigation",
    text: "Use the sidebar to navigate between different features and sections of the platform.",
    attachTo: {
      element: '[data-tour="sidebar-nav"]',
      on: "right",
    },
    showOn() {
      return document.querySelector('[data-tour="sidebar-nav"]') !== null;
    },
    buttons: [
      {
        text: "Back",
        action: tour.back,
        classes: "shepherd-button-secondary",
      },
      {
        text: "Next",
        action: tour.next,
      },
    ],
  });

  // Step 3: Compliance/New Scan menu item
  tour.addStep({
    id: "compliance-scan",
    title: "Start Your First Scan",
    text: "Click 'New Scan' to run your first AI security compliance scan. This is where you'll configure and launch scans.",
    attachTo: {
      element: '[data-tour="compliance-menu"]',
      on: "right",
    },
    showOn() {
      // Initial check: Skip if compliance menu is not visible (e.g., feature flag disabled)
      return document.querySelector('[data-tour="compliance-menu"]') !== null;
    },
    beforeShowPromise() {
      return new Promise<void>((resolve, reject) => {
        const element = document.querySelector('[data-tour="compliance-menu"]');
        if (element && element instanceof HTMLElement && element.offsetParent !== null) {
          resolve();
        } else {
          reject();
        }
      });
    },
    buttons: [
      {
        text: "Back",
        action: tour.back,
        classes: "shepherd-button-secondary",
      },
      {
        text: "Next",
        action: tour.next,
      },
    ],
  });

  // Step 4: Reports quota counter
  tour.addStep({
    id: "quota-counter",
    title: "Track Your Usage",
    text: "Monitor your scan quota here. You can see how many reports you've used and how many remain.",
    attachTo: {
      element: '[data-tour="quota-counter"]',
      on: "bottom",
    },
    showOn() {
      return document.querySelector('[data-tour="quota-counter"]') !== null;
    },
    buttons: [
      {
        text: "Back",
        action: tour.back,
        classes: "shepherd-button-secondary",
      },
      {
        text: "Next",
        action: tour.next,
      },
    ],
  });

  // Step 5: Feedback button
  tour.addStep({
    id: "feedback",
    title: "We're Here to Help",
    text: "Have questions or feedback? Click the 'Send Feedback' button anytime. We'd love to hear from you!",
    attachTo: {
      element: '[data-tour="feedback-button"]',
      on: "bottom",
    },
    showOn() {
      return document.querySelector('[data-tour="feedback-button"]') !== null;
    },
    buttons: [
      {
        text: "Back",
        action: tour.back,
        classes: "shepherd-button-secondary",
      },
      {
        text: "Finish",
        action: tour.complete,
      },
    ],
  });

  return tour;
}
